<template>
    <div class="home">
        <!-- ═══ HERO ═══ -->
        <section class="hp-hero">
            <div class="hp-container hp-hero__inner">
                <div class="hp-hero__text">
                    <h1 class="hp-hero__title">
                        Pregătiri mai ușoare cu
                        <strong>Invitații Smart</strong>.
                    </h1>
                    <p class="hp-hero__subtitle">
                        Creează invitații digitale unice, trimite-le prin SMS sau email
                        și urmărește răspunsurile în timp real — totul dintr-un singur loc.
                    </p>
                    <div class="hp-hero__actions">
                        <RouterLink
                            :to="isAuthenticated ? '/userConfig' : '/register'"
                        >
                            <button class="hp-btn hp-btn--primary hp-btn--lg">
                                Începe Gratuit
                            </button>
                        </RouterLink>
                        <RouterLink to="/howto">
                            <button class="hp-btn hp-btn--outline hp-btn--lg">
                                Cum funcționează?
                            </button>
                        </RouterLink>
                    </div>
                </div>
                <div class="hp-hero__image">
                    <img
                        alt="Invitații Smart — invitații digitale"
                        src="/images/invitatii-banner-1-crop.png"
                    />
                </div>
            </div>
        </section>

        <!-- ═══ STATS BAR ═══ -->
        <section class="hp-stats">
            <div class="hp-container">
                <div class="hp-stats__grid">
                    <div class="hp-stats__item" v-for="stat in stats" :key="stat.label">
                        <div class="hp-stats__icon">
                            <font-awesome-icon :icon="stat.icon" />
                        </div>
                        <div>
                            <p class="hp-stats__label">{{ stat.label }}</p>
                            <p class="hp-stats__desc">{{ stat.desc }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- ═══ TEMPLATE SHOWCASE ═══ -->
        <section class="hp-section hp-section--light" id="templates">
            <div class="hp-container">
                <div class="hp-section__header">
                    <h2 class="hp-section__title">Modele de Invitații</h2>
                    <p class="hp-section__subtitle">
                        Alege un șablon și personalizează-l în câteva minute.
                        Fiecare model este complet editabil.
                    </p>
                </div>
                <div class="hp-tabs" v-if="showcaseTemplates.length">
                    <button
                        class="hp-tabs__btn"
                        :class="{ 'hp-tabs__btn--active': activeCategory === '' }"
                        @click="activeCategory = ''"
                    >Toate</button>
                    <button
                        v-for="cat in showcaseCategories"
                        :key="cat.value"
                        class="hp-tabs__btn"
                        :class="{ 'hp-tabs__btn--active': activeCategory === cat.value }"
                        @click="activeCategory = cat.value"
                    >{{ cat.label }}</button>
                </div>
                <div class="hp-gallery" v-if="filteredShowcase.length">
                    <div
                        class="hp-gallery__card"
                        v-for="tpl in filteredShowcase"
                        :key="tpl.template_id"
                    >
                        <img
                            :src="tpl.template_cover"
                            :alt="tpl.template_name"
                            class="hp-gallery__img"
                        />
                        <div class="hp-gallery__overlay">
                            <span class="hp-gallery__name">{{ tpl.template_name }}</span>
                        </div>
                    </div>
                </div>
                <div v-else-if="!showcaseTemplates.length" class="hp-gallery-placeholder">
                    <font-awesome-icon icon="fa-solid fa-spinner" spin class="text-2xl text-gray-400" />
                </div>
            </div>
        </section>

        <!-- ═══ HOW IT WORKS ═══ -->
        <section class="hp-section" id="how-it-works">
            <div class="hp-container">
                <div class="hp-section__header">
                    <h2 class="hp-section__title">Cum Funcționează</h2>
                    <p class="hp-section__subtitle">
                        De la cont la invitație trimisă — în 4 pași simpli.
                    </p>
                </div>
                <div class="hp-steps">
                    <div class="hp-steps__item" v-for="(step, i) in steps" :key="i">
                        <div class="hp-steps__number">{{ i + 1 }}</div>
                        <h3 class="hp-steps__title">{{ step.title }}</h3>
                        <p class="hp-steps__desc">{{ step.desc }}</p>
                    </div>
                </div>
            </div>
        </section>

        <!-- ═══ FEATURES ═══ -->
        <section class="hp-section hp-section--light" id="features">
            <div class="hp-container">
                <div class="hp-section__header">
                    <h2 class="hp-section__title">Funcționalități</h2>
                    <p class="hp-section__subtitle">
                        Tot ce ai nevoie pentru a crea și gestiona invitații digitale profesionale.
                    </p>
                </div>
                <div class="hp-features">
                    <div class="hp-features__card" v-for="feat in features" :key="feat.title">
                        <div class="hp-features__icon">
                            <font-awesome-icon :icon="feat.icon" />
                        </div>
                        <h3 class="hp-features__title">{{ feat.title }}</h3>
                        <p class="hp-features__desc">{{ feat.desc }}</p>
                    </div>
                </div>
            </div>
        </section>

        <!-- ═══ PRICING PREVIEW ═══ -->
        <section class="hp-section" id="pricing">
            <div class="hp-container">
                <div class="hp-section__header">
                    <h2 class="hp-section__title">Planuri și Prețuri</h2>
                    <p class="hp-section__subtitle">
                        Începe gratuit. Fără card bancar, fără obligații.
                    </p>
                </div>
                <div class="hp-pricing">
                    <!-- Free plan -->
                    <div class="hp-pricing__card">
                        <div class="hp-pricing__badge">Gratuit</div>
                        <h3 class="hp-pricing__name">Explorer</h3>
                        <p class="hp-pricing__price">0 <span>RON</span></p>
                        <ul class="hp-pricing__list">
                            <li>1 proiect</li>
                            <li>Acces la șabloane gratuite</li>
                            <li>Editare text și imagini</li>
                            <li>Partajare link invitație</li>
                        </ul>
                        <RouterLink to="/register">
                            <button class="hp-btn hp-btn--outline w-full">Creează Cont</button>
                        </RouterLink>
                    </div>
                    <!-- Dynamic paid plans -->
                    <div
                        v-for="plan in pricingPlans"
                        :key="plan.id"
                        class="hp-pricing__card"
                        :class="{ 'hp-pricing__card--featured': plan.featured }"
                    >
                        <div v-if="plan.featured" class="hp-pricing__badge hp-pricing__badge--pro">Popular</div>
                        <h3 class="hp-pricing__name">{{ plan.name }}</h3>
                        <p class="hp-pricing__price">{{ plan.price }} <span>RON</span></p>
                        <ul class="hp-pricing__list">
                            <li v-for="feature in plan.features" :key="feature">{{ feature }}</li>
                        </ul>
                        <RouterLink to="/pachete">
                            <button class="hp-btn hp-btn--primary w-full">Alege Pachetul</button>
                        </RouterLink>
                    </div>
                </div>
                <p class="hp-pricing__footer-link">
                    <RouterLink to="/pachete">Vezi toate detaliile și pachetele →</RouterLink>
                </p>
            </div>
        </section>

        <!-- ═══ FINAL CTA ═══ -->
        <section class="hp-cta">
            <div class="hp-container hp-cta__inner">
                <h2 class="hp-cta__title">Pregătit să creezi prima invitație?</h2>
                <p class="hp-cta__subtitle">
                    Alătură-te celor care au ales invitațiile digitale.
                    Contul este gratuit, fără obligații.
                </p>
                <RouterLink :to="isAuthenticated ? '/userConfig' : '/register'">
                    <button class="hp-btn hp-btn--primary hp-btn--lg">
                        Începe Acum — Gratuit
                    </button>
                </RouterLink>
            </div>
        </section>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    name: 'HomeView',
    data() {
        return {
            isAuthenticated: false,
            pricingPlans: [],
            showcaseTemplates: [],
            activeCategory: '',
            categoryLabels: {
                nunta: 'Nuntă',
                botez: 'Botez',
                petrecere: 'Petrecere',
            },
            stats: [
                { icon: 'fa-solid fa-paper-plane', label: 'Partajare', desc: 'Automată' },
                { icon: 'fa-solid fa-fingerprint', label: 'Modele', desc: 'Personalizate' },
                { icon: 'fa-solid fa-chart-pie', label: 'Statistici', desc: 'Avansate' },
            ],
            steps: [
                { title: 'Creează un cont', desc: 'Înregistrează-te gratuit în câteva secunde.' },
                { title: 'Alege un șablon', desc: 'Selectează modelul care ți se potrivește din galeria noastră.' },
                { title: 'Personalizează', desc: 'Editează textul, culorile, imaginile și adaugă stickere.' },
                { title: 'Trimite invitațiile', desc: 'Partajează prin SMS, email sau link direct și urmărește răspunsurile.' },
            ],
            features: [
                { icon: 'fa-solid fa-palette', title: 'Editor Drag & Drop', desc: 'Mută, redimensionează și stilizează elementele invitației direct pe canvas.' },
                { icon: 'fa-solid fa-note-sticky', title: 'Stickere', desc: 'Adaugă stickere decorative pe care le poți roti, redimensiona și oglindi.' },
                { icon: 'fa-solid fa-envelope', title: 'Trimitere SMS & Email', desc: 'Trimite invitațiile direct din platformă, cu mesaje personalizate.' },
                { icon: 'fa-solid fa-chart-line', title: 'Statistici Live', desc: 'Vizualizări, confirmări, refuzuri — totul în timp real pe dashboard.' },
                { icon: 'fa-solid fa-mobile-screen', title: 'Optimizat Mobile', desc: 'Invitațiile arată perfect pe orice dispozitiv, de la telefon la desktop.' },
                { icon: 'fa-solid fa-users', title: 'Gestionare Invitați', desc: 'Adaugă, importă și organizează lista de invitați cu ușurință.' },
            ],
        };
    },
    computed: {
        showcaseCategories() {
            const cats = [...new Set(this.showcaseTemplates.map(t => t.template_category).filter(Boolean))];
            return cats.map(c => ({ value: c, label: this.categoryLabels[c] || c }));
        },
        filteredShowcase() {
            if (!this.activeCategory) return this.showcaseTemplates;
            return this.showcaseTemplates.filter(t => t.template_category === this.activeCategory);
        },
    },
    created() {
        this.isAuthenticated = !!localStorage.getItem('userToken');
        this.fetchShowcase();
        this.fetchPlans();
    },
    methods: {
        async fetchPlans() {
            try {
                const res = await fetch('/api/pricing/getProducts.php');
                const data = await res.json();
                if (data.regular_products) {
                    this.pricingPlans = data.regular_products.map((p, i) => ({
                        id: p.id,
                        name: p.name,
                        price: (p.amount / 100).toFixed(0),
                        features: Object.keys(p.metadata || {}).filter(k => p.metadata[k] === 'true'),
                        featured: i === 0,
                    }));
                }
            } catch (e) {
                console.error('Failed to load pricing plans', e);
            }
        },
        async fetchShowcase() {
            try {
                const res = await axios.get('/api/index.php?endpoint=getTemplateShowcase');
                if (res.data && res.data.success) {
                    this.showcaseTemplates = res.data.data;
                }
            } catch (e) {
                console.error('Failed to load showcase templates', e);
            }
        },
    },
};
</script>

<style scoped>
/* ── Layout ── */
.hp-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 24px;
}

/* ── Hero ── */
.hp-hero {
    padding: 100px 0 40px;
}
.hp-hero__inner {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 48px;
    align-items: center;
}
.hp-hero__title {
    font-size: 2.8rem;
    font-weight: 500;
    line-height: 1.2;
    color: #1e293b;
    margin-bottom: 16px;
}
.hp-hero__title strong {
    color: #f97316;
}
.hp-hero__subtitle {
    font-size: 1.1rem;
    color: #64748b;
    line-height: 1.7;
    margin-bottom: 28px;
}
.hp-hero__actions {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    justify-content: center;
}
.hp-hero__image img {
    width: 100%;
    height: auto;
    border-radius: 12px;
}

/* ── Buttons ── */
.hp-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 10px 24px;
    border-radius: 8px;
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
    border: 2px solid transparent;
    transition: all 0.15s ease;
}
.hp-btn--primary {
    background: #f97316;
    color: #fff;
    border-color: #f97316;
}
.hp-btn--primary:hover {
    background: #ea580c;
    border-color: #ea580c;
    box-shadow: 0 4px 14px rgba(249, 115, 22, 0.35);
}
.hp-btn--outline {
    background: transparent;
    color: #334155;
    border-color: #cbd5e1;
}
.hp-btn--outline:hover {
    border-color: #f97316;
    color: #f97316;
}
.hp-btn--lg {
    padding: 14px 32px;
    font-size: 16px;
}

/* ── Stats bar ── */
.hp-stats {
    padding: 24px 0;
    border-top: 1px solid #f1f5f9;
    border-bottom: 1px solid #f1f5f9;
}
.hp-stats__grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
}
.hp-stats__item {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 12px;
}
.hp-stats__icon {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: #fff7ed;
    color: #f97316;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    flex-shrink: 0;
}
.hp-stats__label {
    font-weight: 700;
    font-size: 15px;
    color: #1e293b;
}
.hp-stats__desc {
    font-size: 13px;
    color: #64748b;
}

/* ── Sections ── */
.hp-section {
    padding: 80px 0;
}
.hp-section--light {
    background: #f8fafc;
}
.hp-section__header {
    text-align: center;
    margin-bottom: 48px;
}
.hp-section__title {
    font-size: 2rem;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 12px;
}
.hp-section__subtitle {
    font-size: 1.05rem;
    color: #64748b;
    max-width: 560px;
    margin: 0 auto;
    line-height: 1.6;
}

/* ── Tabs ── */
.hp-tabs {
    display: flex;
    justify-content: center;
    gap: 8px;
    margin-bottom: 32px;
    flex-wrap: wrap;
}
.hp-tabs__btn {
    padding: 8px 20px;
    border-radius: 24px;
    border: 2px solid #e2e8f0;
    background: #fff;
    color: #64748b;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
}
.hp-tabs__btn:hover {
    border-color: #f97316;
    color: #ea580c;
    background: #fff7ed;
}
.hp-tabs__btn--active {
    background: #f97316;
    border-color: #f97316;
    color: #fff;
}
.hp-tabs__btn--active:hover {
    background: #ea580c;
    border-color: #ea580c;
    color: #fff;
}

/* ── Gallery ── */
.hp-gallery {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 20px;
}
.hp-gallery__card {
    position: relative;
    border-radius: 12px;
    overflow: hidden;
    aspect-ratio: 2 / 3;
    background: #e2e8f0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.hp-gallery__card:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}
.hp-gallery__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}
.hp-gallery__overlay {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 12px;
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.6));
}
.hp-gallery__name {
    color: #fff;
    font-size: 13px;
    font-weight: 600;
}
.hp-gallery-placeholder {
    text-align: center;
    padding: 60px 0;
}

/* ── Steps ── */
.hp-steps {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 32px;
}
.hp-steps__item {
    text-align: center;
}
.hp-steps__number {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: #f97316;
    color: #fff;
    font-size: 20px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 16px;
}
.hp-steps__title {
    font-size: 16px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 8px;
}
.hp-steps__desc {
    font-size: 14px;
    color: #64748b;
    line-height: 1.5;
}

/* ── Features ── */
.hp-features {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 28px;
}
.hp-features__card {
    background: #fff;
    border-radius: 12px;
    padding: 28px 24px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
    transition: box-shadow 0.2s ease;
}
.hp-features__card:hover {
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
}
.hp-features__icon {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    background: #fff7ed;
    color: #f97316;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    margin-bottom: 16px;
}
.hp-features__title {
    font-size: 16px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 8px;
}
.hp-features__desc {
    font-size: 14px;
    color: #64748b;
    line-height: 1.5;
}

/* ── Pricing ── */
.hp-pricing {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 24px;
    max-width: 820px;
    margin: 0 auto;
}
.hp-pricing__card {
    position: relative;
    background: #fff;
    border: 2px solid #e2e8f0;
    border-radius: 16px;
    padding: 32px 28px;
    text-align: center;
    display: flex;
    flex-direction: column;
}
.hp-pricing__card--featured {
    border-color: #f97316;
    box-shadow: 0 4px 20px rgba(249, 115, 22, 0.12);
}
.hp-pricing__badge {
    display: inline-block;
    padding: 3px 12px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background: #f1f5f9;
    color: #64748b;
    margin-bottom: 12px;
    align-self: center;
}
.hp-pricing__badge--pro {
    background: #fff7ed;
    color: #f97316;
}
.hp-pricing__name {
    font-size: 20px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 8px;
}
.hp-pricing__price {
    font-size: 2.5rem;
    font-weight: 800;
    color: #1e293b;
    margin-bottom: 20px;
}
.hp-pricing__price span {
    font-size: 16px;
    font-weight: 500;
    color: #94a3b8;
}
.hp-pricing__list {
    list-style: none;
    padding: 0;
    margin: 0 0 24px;
    text-align: left;
    flex: 1;
}
.hp-pricing__list li {
    padding: 6px 0;
    font-size: 14px;
    color: #475569;
    border-bottom: 1px solid #f1f5f9;
}
.hp-pricing__list li:last-child {
    border-bottom: none;
}
.hp-pricing__list li::before {
    content: '✓';
    color: #22c55e;
    font-weight: 700;
    margin-right: 8px;
}
.hp-pricing__footer-link {
    text-align: center;
    margin-top: 20px;
    font-size: 14px;
}
.hp-pricing__footer-link a {
    color: #f97316;
    font-weight: 600;
    text-decoration: none;
}
.hp-pricing__footer-link a:hover {
    text-decoration: underline;
}

/* ── CTA ── */
.hp-cta {
    padding: 80px 0;
    background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
}
.hp-cta__inner {
    text-align: center;
}
.hp-cta__title {
    font-size: 2rem;
    font-weight: 700;
    color: #fff;
    margin-bottom: 12px;
}
.hp-cta__subtitle {
    font-size: 1.05rem;
    color: #94a3b8;
    max-width: 480px;
    margin: 0 auto 28px;
    line-height: 1.6;
}

/* ── Responsive ── */
@media (max-width: 768px) {
    .hp-hero {
        padding: 60px 0 40px;
    }
    .hp-hero__inner {
        grid-template-columns: 1fr;
        gap: 32px;
        text-align: center;
    }
    .hp-hero__title {
        font-size: 2rem;
    }
    .hp-hero__actions {
        justify-content: center;
    }
    .hp-hero__image {
        order: -1;
    }
    .hp-stats__grid {
        grid-template-columns: 1fr;
        gap: 8px;
    }
    .hp-steps {
        grid-template-columns: 1fr 1fr;
        gap: 24px;
    }
    .hp-features {
        grid-template-columns: 1fr;
    }
    .hp-pricing {
        grid-template-columns: 1fr;
        max-width: 360px;
    }
    .hp-gallery {
        grid-template-columns: repeat(2, 1fr);
    }
}
</style>
